import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { usePolling } from "../hooks/usePolling";
import { History } from "lucide-react";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Actually refetches from server — fixes the old useless re-render interval
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.filter(n => !n.read));
    } catch {}
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/notifications/history");
      setHistory(data);
    } catch {}
  };

  // Initial load
  useEffect(() => { load(); }, [load]);


  // Poll every 20s — notifications page doesn't need to be as aggressive as job stats
  usePolling(load, 20000, true);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems([]);
  };

  const toggleHistory = async () => {
    if (!showHistory) await loadHistory();
    setShowHistory(s => !s);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8" data-testid="notifications-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl tracking-tighter">Notifications</h1>
        <div className="flex gap-2">
          <button onClick={toggleHistory} className="btn-outline text-xs !py-2 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> {showHistory ? "Hide" : "View"} history
          </button>
          {items.length > 0 && (
            <button onClick={markAll} className="btn-outline text-xs !py-2">Mark all read</button>
          )}
        </div>
      </div>

      {items.length === 0 && !showHistory
        ? <p className="text-sm text-muted-foreground">No new notifications.</p>
        : (
          <div className="space-y-2">
            {items.map(n => (
              <Link key={n.id} onClick={() => markRead(n.id)} to={n.link || "#"}
                className="card-flat block border-brand/40 bg-brand-tint/30 hover:shadow-md transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{n.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{n.message}</p>
                  </div>
                  <span className="w-2 h-2 rounded-full bg-brand flex-shrink-0 mt-1.5" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">{timeAgo(n.created_at)}</p>
              </Link>
            ))}
          </div>
        )}

      {showHistory && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-display text-xl">History <span className="text-sm text-muted-foreground font-normal">(last 7 days)</span></h2>
          </div>
          {history.length === 0
            ? <p className="text-sm text-muted-foreground">No notification history.</p>
            : (
              <div className="space-y-2 opacity-70">
                {history.map(n => (
                  <Link key={n.id} to={n.link || "#"} className="card-flat block hover:opacity-100 transition">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{n.title}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{timeAgo(n.created_at)}</p>
                  </Link>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
